import { normalizeLocale, type SupportedLocale } from './config';
import { PENDING_UPLOAD_SEND_EASTERN_COPY } from './pending-upload-send-copy-eastern';

type Plural = { one: string; other: string };

/** The waiting first message and the background-upload pill (#4844). */
export type PendingUploadSendCopy = {
  /** `{count}` files of the message are still uploading. */
  uploadingFiles: Plural;
  /** `{count}` files failed; the message waits on them. */
  needsAttention: Plural;
  heldUntilFixed: string;
  /** The sender's files are uploading on a device that is not this one. */
  waitingOtherDevice: string;
  /** Every file is in and the task is starting. */
  starting: string;
  /** The server refused to start the task (credits, access). */
  startFailed: string;
  /** Screen readers: a file that finished uploading. */
  fileReady: string;
  /** A file waiting on the device it was sent from. */
  fileWaiting: string;
  /** Every byte is sent; the server is verifying and saving the files. */
  finishing: string;
  /** One file's bytes are all sent; the server is saving it. */
  fileFinishing: string;
  failureNetwork: string;
  failureServer: string;
  failureTooLarge: string;
  failureUnavailable: string;
  failureUnknown: string;
  retry: string;
  /** Drop a message that couldn't start, freeing the task. */
  discard: string;
  remove: string;
  /** `{name}` is the file name. */
  retryFile: string;
  /** `{name}` is the file name. */
  removeFile: string;
  /** Screen readers: `{percent}` of the files are uploaded. */
  progressLabel: string;
  /** The app-wide pill; `{count}` files uploading. */
  backgroundUploading: Plural;
  /** The app-wide pill; `{count}` failed uploads. */
  backgroundNeedsAttention: Plural;
  /** The app-wide pill; `{count}` uploads with every byte sent, saving. */
  backgroundFinishing: Plural;
  /** Toast: a Retry or Remove didn't reach the server. */
  actionFailed: string;
  /** Screen readers: the pill opens the task the files belong to. */
  openTask: string;
};

const WESTERN_COPY = {
  en: {
    uploadingFiles: {
      one: 'Uploading {count} file…',
      other: 'Uploading {count} files…',
    },
    needsAttention: {
      one: "{count} file didn't upload.",
      other: "{count} files didn't upload.",
    },
    heldUntilFixed: 'Your message sends once every file is in or removed.',
    waitingOtherDevice:
      'Waiting for your files to finish uploading. Open IMAI on the device you sent from to continue.',
    starting: 'Starting…',
    startFailed: "Couldn't start this task.",
    fileReady: 'Uploaded',
    fileWaiting: 'Waiting',
    finishing: 'Finishing…',
    fileFinishing: 'Saving…',
    failureNetwork: 'The connection dropped.',
    failureServer: "It didn't finish uploading.",
    failureTooLarge: 'Over the 2 GB upload limit.',
    failureUnavailable: 'Needs to be attached again.',
    failureUnknown: "Didn't upload.",
    retry: 'Retry',
    discard: 'Discard',
    remove: 'Remove',
    retryFile: 'Retry {name}',
    removeFile: 'Remove {name}',
    progressLabel: '{percent} uploaded',
    backgroundUploading: {
      one: 'Uploading {count} file in the background',
      other: 'Uploading {count} files in the background',
    },
    backgroundNeedsAttention: {
      one: '{count} upload needs attention',
      other: '{count} uploads need attention',
    },
    backgroundFinishing: {
      one: 'Finishing {count} upload…',
      other: 'Finishing {count} uploads…',
    },
    actionFailed: "That didn't go through. Try again.",
    openTask: 'Open the task',
  },
  es: {
    uploadingFiles: {
      one: 'Subiendo {count} archivo…',
      other: 'Subiendo {count} archivos…',
    },
    needsAttention: {
      one: '{count} archivo no se subió.',
      other: '{count} archivos no se subieron.',
    },
    heldUntilFixed:
      'Tu mensaje se envía cuando todos los archivos estén subidos o quitados.',
    waitingOtherDevice:
      'Esperando a que terminen de subirse tus archivos. Abre IMAI en el dispositivo desde el que enviaste para continuar.',
    starting: 'Iniciando…',
    startFailed: 'No se pudo iniciar esta tarea.',
    fileReady: 'Subido',
    fileWaiting: 'En espera',
    finishing: 'Finalizando…',
    fileFinishing: 'Guardando…',
    failureNetwork: 'Se perdió la conexión.',
    failureServer: 'No terminó de subirse.',
    failureTooLarge: 'Supera el límite de 2 GB.',
    failureUnavailable: 'Hay que adjuntarlo de nuevo.',
    failureUnknown: 'No se subió.',
    retry: 'Reintentar',
    discard: 'Descartar',
    remove: 'Quitar',
    retryFile: 'Reintentar {name}',
    removeFile: 'Quitar {name}',
    progressLabel: '{percent} subido',
    backgroundUploading: {
      one: 'Subiendo {count} archivo en segundo plano',
      other: 'Subiendo {count} archivos en segundo plano',
    },
    backgroundNeedsAttention: {
      one: '{count} subida necesita atención',
      other: '{count} subidas necesitan atención',
    },
    backgroundFinishing: {
      one: 'Finalizando {count} subida…',
      other: 'Finalizando {count} subidas…',
    },
    actionFailed: 'No se pudo completar. Inténtalo de nuevo.',
    openTask: 'Abrir la tarea',
  },
  'pt-BR': {
    uploadingFiles: {
      one: 'Enviando {count} arquivo…',
      other: 'Enviando {count} arquivos…',
    },
    needsAttention: {
      one: '{count} arquivo não foi enviado.',
      other: '{count} arquivos não foram enviados.',
    },
    heldUntilFixed:
      'Sua mensagem é enviada quando todos os arquivos forem enviados ou removidos.',
    waitingOtherDevice:
      'Aguardando o envio dos seus arquivos. Abra o IMAI no dispositivo de onde você enviou para continuar.',
    starting: 'Iniciando…',
    startFailed: 'Não foi possível iniciar esta tarefa.',
    fileReady: 'Enviado',
    fileWaiting: 'Aguardando',
    finishing: 'Finalizando…',
    fileFinishing: 'Salvando…',
    failureNetwork: 'A conexão caiu.',
    failureServer: 'Não terminou de ser enviado.',
    failureTooLarge: 'Acima do limite de 2 GB.',
    failureUnavailable: 'Precisa ser anexado de novo.',
    failureUnknown: 'Não foi enviado.',
    retry: 'Tentar de novo',
    discard: 'Descartar',
    remove: 'Remover',
    retryFile: 'Tentar enviar {name} de novo',
    removeFile: 'Remover {name}',
    progressLabel: '{percent} enviado',
    backgroundUploading: {
      one: 'Enviando {count} arquivo em segundo plano',
      other: 'Enviando {count} arquivos em segundo plano',
    },
    backgroundNeedsAttention: {
      one: '{count} envio precisa de atenção',
      other: '{count} envios precisam de atenção',
    },
    backgroundFinishing: {
      one: 'Finalizando {count} envio…',
      other: 'Finalizando {count} envios…',
    },
    actionFailed: 'Não deu certo. Tente de novo.',
    openTask: 'Abrir a tarefa',
  },
  fr: {
    uploadingFiles: {
      one: 'Envoi de {count} fichier…',
      other: 'Envoi de {count} fichiers…',
    },
    needsAttention: {
      one: "{count} fichier n'a pas été envoyé.",
      other: "{count} fichiers n'ont pas été envoyés.",
    },
    heldUntilFixed:
      'Votre message part dès que chaque fichier est envoyé ou retiré.',
    waitingOtherDevice:
      "En attente de la fin de l'envoi de vos fichiers. Ouvrez IMAI sur l'appareil d'envoi pour continuer.",
    starting: 'Démarrage…',
    startFailed: 'Impossible de démarrer cette tâche.',
    fileReady: 'Envoyé',
    fileWaiting: 'En attente',
    finishing: 'Finalisation…',
    fileFinishing: 'Enregistrement…',
    failureNetwork: 'La connexion a été coupée.',
    failureServer: "Son envoi n'a pas abouti.",
    failureTooLarge: 'Dépasse la limite de 2 Go.',
    failureUnavailable: 'Doit être joint à nouveau.',
    failureUnknown: "N'a pas été envoyé.",
    retry: 'Réessayer',
    discard: 'Abandonner',
    remove: 'Retirer',
    retryFile: 'Réessayer {name}',
    removeFile: 'Retirer {name}',
    progressLabel: '{percent} envoyé',
    backgroundUploading: {
      one: 'Envoi de {count} fichier en arrière-plan',
      other: 'Envoi de {count} fichiers en arrière-plan',
    },
    backgroundNeedsAttention: {
      one: '{count} envoi demande votre attention',
      other: '{count} envois demandent votre attention',
    },
    backgroundFinishing: {
      one: 'Finalisation de {count} envoi…',
      other: 'Finalisation de {count} envois…',
    },
    actionFailed: "Ça n'a pas fonctionné. Réessayez.",
    openTask: 'Ouvrir la tâche',
  },
  it: {
    uploadingFiles: {
      one: 'Caricamento di {count} file…',
      other: 'Caricamento di {count} file…',
    },
    needsAttention: {
      one: '{count} file non è stato caricato.',
      other: '{count} file non sono stati caricati.',
    },
    heldUntilFixed: 'Il messaggio parte quando ogni file è caricato o rimosso.',
    waitingOtherDevice:
      'In attesa che i tuoi file finiscano di caricarsi. Apri IMAI sul dispositivo da cui hai inviato per continuare.',
    starting: 'Avvio…',
    startFailed: 'Impossibile avviare questa attività.',
    fileReady: 'Caricato',
    fileWaiting: 'In attesa',
    finishing: 'Completamento…',
    fileFinishing: 'Salvataggio…',
    failureNetwork: 'La connessione si è interrotta.',
    failureServer: 'Il caricamento non è stato completato.',
    failureTooLarge: 'Supera il limite di 2 GB.',
    failureUnavailable: 'Va allegato di nuovo.',
    failureUnknown: 'Non caricato.',
    retry: 'Riprova',
    discard: 'Scarta',
    remove: 'Rimuovi',
    retryFile: 'Riprova {name}',
    removeFile: 'Rimuovi {name}',
    progressLabel: '{percent} caricato',
    backgroundUploading: {
      one: 'Caricamento di {count} file in background',
      other: 'Caricamento di {count} file in background',
    },
    backgroundNeedsAttention: {
      one: '{count} caricamento richiede attenzione',
      other: '{count} caricamenti richiedono attenzione',
    },
    backgroundFinishing: {
      one: 'Completamento di {count} caricamento…',
      other: 'Completamento di {count} caricamenti…',
    },
    actionFailed: 'Non è andato a buon fine. Riprova.',
    openTask: "Apri l'attività",
  },
  de: {
    uploadingFiles: {
      one: '{count} Datei wird hochgeladen…',
      other: '{count} Dateien werden hochgeladen…',
    },
    needsAttention: {
      one: '{count} Datei wurde nicht hochgeladen.',
      other: '{count} Dateien wurden nicht hochgeladen.',
    },
    heldUntilFixed:
      'Deine Nachricht wird gesendet, sobald jede Datei hochgeladen oder entfernt ist.',
    waitingOtherDevice:
      'Warte, bis deine Dateien hochgeladen sind. Öffne IMAI auf dem Gerät, von dem du gesendet hast, um fortzufahren.',
    starting: 'Wird gestartet…',
    startFailed: 'Diese Aufgabe konnte nicht gestartet werden.',
    fileReady: 'Hochgeladen',
    fileWaiting: 'Wartet',
    finishing: 'Wird abgeschlossen…',
    fileFinishing: 'Wird gespeichert…',
    failureNetwork: 'Die Verbindung wurde unterbrochen.',
    failureServer: 'Der Upload wurde nicht abgeschlossen.',
    failureTooLarge: 'Über dem Limit von 2 GB.',
    failureUnavailable: 'Muss erneut angehängt werden.',
    failureUnknown: 'Nicht hochgeladen.',
    retry: 'Erneut versuchen',
    discard: 'Verwerfen',
    remove: 'Entfernen',
    retryFile: '{name} erneut versuchen',
    removeFile: '{name} entfernen',
    progressLabel: '{percent} hochgeladen',
    backgroundUploading: {
      one: '{count} Datei wird im Hintergrund hochgeladen',
      other: '{count} Dateien werden im Hintergrund hochgeladen',
    },
    backgroundNeedsAttention: {
      one: '{count} Upload braucht deine Hilfe',
      other: '{count} Uploads brauchen deine Hilfe',
    },
    backgroundFinishing: {
      one: '{count} Upload wird abgeschlossen…',
      other: '{count} Uploads werden abgeschlossen…',
    },
    actionFailed: 'Das hat nicht geklappt. Versuch es noch einmal.',
    openTask: 'Aufgabe öffnen',
  },
  vi: {
    uploadingFiles: {
      one: 'Đang tải lên {count} tệp…',
      other: 'Đang tải lên {count} tệp…',
    },
    needsAttention: {
      one: '{count} tệp chưa tải lên được.',
      other: '{count} tệp chưa tải lên được.',
    },
    heldUntilFixed: 'Tin nhắn sẽ được gửi khi mọi tệp đã tải lên hoặc đã xóa.',
    waitingOtherDevice:
      'Đang chờ tệp của bạn tải lên xong. Mở IMAI trên thiết bị bạn đã gửi để tiếp tục.',
    starting: 'Đang bắt đầu…',
    startFailed: 'Không thể bắt đầu tác vụ này.',
    fileReady: 'Đã tải lên',
    fileWaiting: 'Đang chờ',
    finishing: 'Đang hoàn tất…',
    fileFinishing: 'Đang lưu…',
    failureNetwork: 'Kết nối bị gián đoạn.',
    failureServer: 'Tệp chưa tải lên xong.',
    failureTooLarge: 'Vượt quá giới hạn 2 GB.',
    failureUnavailable: 'Cần đính kèm lại.',
    failureUnknown: 'Chưa tải lên được.',
    retry: 'Thử lại',
    discard: 'Bỏ',
    remove: 'Xóa',
    retryFile: 'Thử lại {name}',
    removeFile: 'Xóa {name}',
    progressLabel: 'Đã tải lên {percent}',
    backgroundUploading: {
      one: 'Đang tải lên {count} tệp trong nền',
      other: 'Đang tải lên {count} tệp trong nền',
    },
    backgroundNeedsAttention: {
      one: '{count} lượt tải lên cần xử lý',
      other: '{count} lượt tải lên cần xử lý',
    },
    backgroundFinishing: {
      one: 'Đang hoàn tất {count} tệp tải lên…',
      other: 'Đang hoàn tất {count} tệp tải lên…',
    },
    actionFailed: 'Chưa thực hiện được. Hãy thử lại.',
    openTask: 'Mở tác vụ',
  },
  id: {
    uploadingFiles: {
      one: 'Mengunggah {count} file…',
      other: 'Mengunggah {count} file…',
    },
    needsAttention: {
      one: '{count} file gagal diunggah.',
      other: '{count} file gagal diunggah.',
    },
    heldUntilFixed:
      'Pesanmu dikirim setelah semua file terunggah atau dihapus.',
    waitingOtherDevice:
      'Menunggu file selesai diunggah. Buka IMAI di perangkat yang kamu pakai untuk mengirim agar bisa lanjut.',
    starting: 'Memulai…',
    startFailed: 'Tugas ini tidak bisa dimulai.',
    fileReady: 'Terunggah',
    fileWaiting: 'Menunggu',
    finishing: 'Menyelesaikan…',
    fileFinishing: 'Menyimpan…',
    failureNetwork: 'Koneksi terputus.',
    failureServer: 'File belum selesai diunggah.',
    failureTooLarge: 'Melebihi batas 2 GB.',
    failureUnavailable: 'Perlu dilampirkan lagi.',
    failureUnknown: 'Gagal diunggah.',
    retry: 'Coba lagi',
    discard: 'Buang',
    remove: 'Hapus',
    retryFile: 'Coba lagi {name}',
    removeFile: 'Hapus {name}',
    progressLabel: '{percent} terunggah',
    backgroundUploading: {
      one: 'Mengunggah {count} file di latar belakang',
      other: 'Mengunggah {count} file di latar belakang',
    },
    backgroundNeedsAttention: {
      one: '{count} unggahan perlu perhatian',
      other: '{count} unggahan perlu perhatian',
    },
    backgroundFinishing: {
      one: 'Menyelesaikan {count} unggahan…',
      other: 'Menyelesaikan {count} unggahan…',
    },
    actionFailed: 'Belum berhasil. Coba lagi.',
    openTask: 'Buka tugas',
  },
} satisfies Partial<Record<SupportedLocale, PendingUploadSendCopy>>;

const COPY: Record<SupportedLocale, PendingUploadSendCopy> = {
  ...WESTERN_COPY,
  ...PENDING_UPLOAD_SEND_EASTERN_COPY,
};

export function getPendingUploadSendCopy(
  locale?: string | null
): PendingUploadSendCopy {
  return COPY[normalizeLocale(locale)];
}

/** Picks the plural form and fills `{count}`. */
export function formatPendingUploadCount(
  forms: Plural,
  count: number,
  locale?: string | null
): string {
  const form =
    new Intl.PluralRules(normalizeLocale(locale)).select(count) === 'one'
      ? forms.one
      : forms.other;
  return form.replace(
    '{count}',
    new Intl.NumberFormat(normalizeLocale(locale)).format(count)
  );
}
